// Firestore write helpers used by dashboard forms. Mirror firestore.rules:
//   - Team create is allowed for any signed-in user (becomes owner).
//   - Member writes (invites + role changes) are admin/owner-only.
//   - Project + flow writes are admin/owner-only.

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { db } from './firebase.js'

export const KNOWN_TASKS = ['graphExtraction', 'driftSummary', 'nlSearch', 'fingerprintVision']

/** @param {string} s */
const safeId = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)

/**
 * @param {{ name: string, ownerUid: string, ownerEmail?: string | null, ownerDisplayName?: string | null }} args
 */
export async function createTeam({ name, ownerUid, ownerEmail, ownerDisplayName }) {
  if (!ownerUid) throw new Error('createTeam: ownerUid required')
  if (!name?.trim()) throw new Error('createTeam: name required')

  const teamId = `team_${safeId(name)}_${Date.now().toString(36)}`
  const batch = writeBatch(db)
  batch.set(doc(db, 'teams', teamId), {
    name,
    createdAt: serverTimestamp(),
    createdBy: ownerUid,
  })
  batch.set(doc(db, 'teams', teamId, 'members', ownerUid), {
    uid: ownerUid,
    role: 'owner',
    email: ownerEmail ?? null,
    displayName: ownerDisplayName ?? null,
    joinedAt: serverTimestamp(),
  })
  await batch.commit()
  return teamId
}

/**
 * @param {{ teamId: string, name: string }} args
 */
export async function createProject({ teamId, name }) {
  if (!teamId) throw new Error('createProject: teamId required')
  if (!name?.trim()) throw new Error('createProject: name required')

  const projectId = `cairn_proj_${safeId(name)}_${Date.now().toString(36)}`
  await setDoc(doc(db, 'teams', teamId, 'projects', projectId), {
    name,
    createdAt: serverTimestamp(),
  })
  return projectId
}

/**
 * @param {{ teamId: string, projectId: string, name: string, yaml: string }} args
 */
export async function createFlow({ teamId, projectId, name, yaml }) {
  if (!teamId || !projectId) throw new Error('createFlow: teamId + projectId required')
  if (!name?.trim() || !yaml?.trim()) throw new Error('createFlow: name + yaml required')

  const flowId = `flow_${safeId(name)}_${Date.now().toString(36)}`
  await setDoc(doc(db, 'teams', teamId, 'projects', projectId, 'flows', flowId), {
    name,
    yaml,
    tags: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return flowId
}

/**
 * @param {{ teamId: string, projectId: string, flowId: string, yaml?: string, name?: string, tags?: string[] }} args
 */
export async function updateFlow({ teamId, projectId, flowId, yaml, name, tags }) {
  if (!teamId || !projectId || !flowId) {
    throw new Error('updateFlow: teamId + projectId + flowId required')
  }
  /** @type {{ updatedAt: import('firebase/firestore').FieldValue, yaml?: string, name?: string, tags?: string[] }} */
  const patch = { updatedAt: serverTimestamp() }
  if (yaml !== undefined) patch.yaml = yaml
  if (name !== undefined) patch.name = name
  if (tags !== undefined) patch.tags = tags
  await updateDoc(doc(db, 'teams', teamId, 'projects', projectId, 'flows', flowId), patch)
}

/**
 * @param {{ teamId: string, invitedEmail: string, role?: string, invitedByUid?: string | null }} args
 */
export async function inviteMember({ teamId, invitedEmail, role, invitedByUid }) {
  if (!teamId) throw new Error('inviteMember: teamId required')
  if (!invitedEmail?.trim()) throw new Error('inviteMember: invitedEmail required')
  const inviteId = `invite_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
  await setDoc(doc(db, 'teams', teamId, 'invites', inviteId), {
    invitedEmail: invitedEmail.trim().toLowerCase(),
    role: role ?? 'member',
    invitedBy: invitedByUid ?? null,
    createdAt: serverTimestamp(),
    status: 'pending',
  })
  return inviteId
}

/**
 * @param {{ teamId: string, inviteId: string }} args
 */
export async function revokeInvite({ teamId, inviteId }) {
  if (!teamId || !inviteId) throw new Error('revokeInvite: teamId + inviteId required')
  await deleteDoc(doc(db, 'teams', teamId, 'invites', inviteId))
}

/**
 * @param {{ teamId: string, memberUid: string, role: string }} args
 */
export async function changeMemberRole({ teamId, memberUid, role }) {
  if (!teamId || !memberUid) throw new Error('changeMemberRole: teamId + memberUid required')
  if (!['owner', 'admin', 'member'].includes(role)) {
    throw new Error(`changeMemberRole: invalid role "${role}"`)
  }
  await updateDoc(doc(db, 'teams', teamId, 'members', memberUid), { role })
}

/**
 * @param {{ teamId: string, memberUid: string }} args
 */
export async function removeMember({ teamId, memberUid }) {
  if (!teamId || !memberUid) throw new Error('removeMember: teamId + memberUid required')
  await deleteDoc(doc(db, 'teams', teamId, 'members', memberUid))
}

/**
 * Look up the current user's role on a team. Returns null when not a member.
 * @param {{ teamId: string, uid: string }} args
 */
export async function getCurrentRole({ teamId, uid }) {
  if (!teamId || !uid) return null
  const snap = await getDoc(doc(db, 'teams', teamId, 'members', uid))
  return snap.exists() ? (snap.data().role ?? null) : null
}

/**
 * Set or clear the per-task model override on a team. Pass `modelId = null`
 * to revert the task to its Cairn-default fallback chain (the override is
 * removed from the team doc via deleteField).
 * @param {{ teamId: string, task: string, modelId?: string | null }} args
 */
export async function setModelOverride({ teamId, task, modelId }) {
  if (!teamId) throw new Error('setModelOverride: teamId required')
  if (!KNOWN_TASKS.includes(task)) {
    throw new Error(`setModelOverride: invalid task "${task}"`)
  }
  const ref = doc(db, 'teams', teamId)
  const fieldPath = `openrouter.modelOverrides.${task}`
  // deleteField() only triggers an unset on updateDoc with dot-notation paths.
  // The team doc always exists (createTeam writes it as a batched first
  // write), so updateDoc is safe here.
  await updateDoc(ref, {
    [fieldPath]: modelId ?? deleteField(),
    updatedAt: serverTimestamp(),
  })
  return fieldPath
}

/**
 * Mark a drift as accepted / dismissed / pending. The dashboard only writes
 * the limited review fields (status / reviewedBy / reviewedAt) — the rest
 * of the drift body stays immutable per firestore.rules. The actual YAML
 * patch (CST edit via eemeli/yaml) happens via the worker's accept handler
 * or the local `cairn accept` CLI; this mutation just records intent.
 * @param {{ teamId: string, projectId: string, scanId: string, driftId: string, status: string, reviewerUid?: string | null }} args
 */
export async function setDriftStatus({ teamId, projectId, scanId, driftId, status, reviewerUid }) {
  if (!teamId || !projectId || !scanId || !driftId) {
    throw new Error('setDriftStatus: teamId + projectId + scanId + driftId required')
  }
  if (!['pending', 'accepted', 'dismissed'].includes(status)) {
    throw new Error(`setDriftStatus: invalid status "${status}"`)
  }
  const ref = doc(db, 'teams', teamId, 'projects', projectId, 'scans', scanId, 'drifts', driftId)
  await updateDoc(ref, {
    status,
    reviewedBy: reviewerUid ?? null,
    reviewedAt: serverTimestamp(),
  })
}

/**
 * Batch-update many drifts at once. Used by the "Accept all high-confidence"
 * action. Firestore caps writeBatch at 500 ops — for larger sets we'd chunk;
 * V1 drift counts are well below the cap.
 * @param {{ teamId: string, projectId: string, scanId: string, driftIds: string[], status: string, reviewerUid?: string | null }} args
 */
export async function setDriftStatusBatch({
  teamId,
  projectId,
  scanId,
  driftIds,
  status,
  reviewerUid,
}) {
  if (!teamId || !projectId || !scanId) {
    throw new Error('setDriftStatusBatch: teamId + projectId + scanId required')
  }
  if (!Array.isArray(driftIds) || driftIds.length === 0) {
    throw new Error('setDriftStatusBatch: non-empty driftIds required')
  }
  if (!['pending', 'accepted', 'dismissed'].includes(status)) {
    throw new Error(`setDriftStatusBatch: invalid status "${status}"`)
  }
  const batch = writeBatch(db)
  for (const driftId of driftIds) {
    const ref = doc(db, 'teams', teamId, 'projects', projectId, 'scans', scanId, 'drifts', driftId)
    batch.update(ref, {
      status,
      reviewedBy: reviewerUid ?? null,
      reviewedAt: serverTimestamp(),
    })
  }
  await batch.commit()
  return driftIds.length
}

export { collection }
